import React, { useState, useEffect } from 'react';
import { getCandidates, downloadReport, sendReminder } from '../services/adminApi';
import { EyeIcon, DownloadIcon, MailIcon } from '@heroicons/react/outline';

const CandidateManagement = () => {
    const [candidates, setCandidates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({ status: '', search: '' });
    const [pagination, setPagination] = useState({ limit: 20, offset: 0, total: 0 });

    useEffect(() => {
        fetchCandidates();
    }, [filters, pagination.offset]);

    const fetchCandidates = async () => {
        setLoading(true);
        try {
            const response = await getCandidates({
                ...filters,
                limit: pagination.limit,
                offset: pagination.offset
            });
            setCandidates(response.data.candidates || []);
            setPagination(prev => ({ ...prev, total: response.data.total || 0 }));
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadReport = async (candidateId) => {
        try {
            const response = await downloadReport(candidateId);
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `candidate_report_${candidateId}.pdf`;
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error(error);
            alert('Failed to download report');
        }
    };

    const handleSendReminder = async (candidateId) => {
        try {
            await sendReminder(candidateId);
            alert('Reminder sent successfully');
        } catch (error) {
            console.error(error);
            alert('Failed to send reminder');
        }
    };

    const getStatusBadge = (status) => {
        const colors = {
            pending: 'bg-yellow-100 text-yellow-800',
            started: 'bg-blue-100 text-blue-800',
            completed: 'bg-green-100 text-green-800'
        };
        return (
            <span className={`px-2 py-1 text-xs rounded-full ${colors[status] || 'bg-gray-100'}`}>
                {status || 'unknown'}
            </span>
        );
    };

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-gray-800">Candidates</h1>
            </div>

            <div className="bg-white rounded-lg shadow p-4 mb-6">
                <div className="flex flex-wrap gap-4">
                    <div className="flex-1 min-w-[200px]">
                        <input
                            type="text"
                            placeholder="Search by name or email..."
                            value={filters.search}
                            onChange={(e) => setFilters({ ...filters, search: e.target.value, offset: 0 })}
                            className="w-full border rounded-lg px-4 py-2"
                        />
                    </div>
                    <div className="w-48">
                        <select
                            value={filters.status}
                            onChange={(e) => setFilters({ ...filters, status: e.target.value, offset: 0 })}
                            className="w-full border rounded-lg px-4 py-2"
                        >
                            <option value="">All Status</option>
                            <option value="pending">Pending</option>
                            <option value="started">Started</option>
                            <option value="completed">Completed</option>
                        </select>
                    </div>
                    <button
                        onClick={() => setFilters({ status: '', search: '', offset: 0 })}
                        className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                    >
                        Clear
                    </button>
                </div>
            </div>

            <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assessment</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Started</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Completed</th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {loading ? (
                                <tr>
                                    <td colSpan="7" className="px-6 py-12 text-center">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
                                    </td>
                                </tr>
                            ) : candidates.length === 0 ? (
                                <tr>
                                    <td colSpan="7" className="px-6 py-12 text-center text-gray-400">
                                        No candidates found
                                    </td>
                                </tr>
                            ) : (
                                candidates.map(candidate => (
                                    <tr key={candidate.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4">
                                            <div className="font-medium text-gray-900">{candidate.candidate_name}</div>
                                        </td>
                                        <td className="px-6 py-4 text-gray-600">{candidate.candidate_email}</td>
                                        <td className="px-6 py-4 text-gray-600">{candidate.assessment_name}</td>
                                        <td className="px-6 py-4">{getStatusBadge(candidate.status)}</td>
                                        <td className="px-6 py-4 text-gray-600">
                                            {candidate.started_at ? new Date(candidate.started_at).toLocaleDateString() : '-'}
                                        </td>
                                        <td className="px-6 py-4 text-gray-600">
                                            {candidate.completed_at ? new Date(candidate.completed_at).toLocaleDateString() : '-'}
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex justify-end space-x-2">
                                                <button
                                                    onClick={() => window.open(`/admin/candidates/${candidate.id}`, '_blank')}
                                                    className="p-1 text-blue-600 hover:text-blue-800"
                                                    title="View Details"
                                                >
                                                    <EyeIcon className="h-5 w-5" />
                                                </button>
                                                {candidate.status === 'completed' && (
                                                    <button
                                                        onClick={() => handleDownloadReport(candidate.id)}
                                                        className="p-1 text-green-600 hover:text-green-800"
                                                        title="Download Report"
                                                    >
                                                        <DownloadIcon className="h-5 w-5" />
                                                    </button>
                                                )}
                                                {candidate.status !== 'completed' && (
                                                    <button
                                                        onClick={() => handleSendReminder(candidate.id)}
                                                        className="p-1 text-yellow-600 hover:text-yellow-800"
                                                        title="Send Reminder"
                                                    >
                                                        <MailIcon className="h-5 w-5" />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {pagination.total > pagination.limit && (
                    <div className="px-6 py-4 border-t flex justify-between items-center">
                        <div className="text-sm text-gray-500">
                            Showing {pagination.offset + 1} to {Math.min(pagination.offset + pagination.limit, pagination.total)} of {pagination.total}
                        </div>
                        <div className="flex space-x-2">
                            <button
                                onClick={() => setPagination(prev => ({ ...prev, offset: Math.max(0, prev.offset - prev.limit) }))}
                                disabled={pagination.offset === 0}
                                className="px-3 py-1 border rounded disabled:opacity-50"
                            >
                                Previous
                            </button>
                            <button
                                onClick={() => setPagination(prev => ({ ...prev, offset: prev.offset + prev.limit }))}
                                disabled={pagination.offset + pagination.limit >= pagination.total}
                                className="px-3 py-1 border rounded disabled:opacity-50"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CandidateManagement;